import React, { useState, useCallback, useMemo, useEffect } from 'react';

const STORAGE_KEY = 'tef-brutto-netto-saved';

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function fmtEuro(v) {
  return v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

const MWST_OPTIONS = [
  { label: '19% (Standard)', value: 19 },
  { label: '7% (ermäßigt)', value: 7 },
  { label: '0% (befreit)', value: 0 },
];

// ========== LIQUID GLASS CARD ==========
function LiquidGlassCard({ children, glow = 'cyan', style = {} }) {
  const glowColors = {
    cyan: { border: 'rgba(0,245,212,0.18)', bg: 'rgba(0,245,212,0.03)', shadow: 'rgba(0,245,212,0.08)' },
    pink: { border: 'rgba(255,0,110,0.18)', bg: 'rgba(255,0,110,0.03)', shadow: 'rgba(255,0,110,0.08)' },
    purple: { border: 'rgba(191,90,242,0.18)', bg: 'rgba(191,90,242,0.03)', shadow: 'rgba(191,90,242,0.08)' },
    dual: { border: 'rgba(0,245,212,0.15)', bg: 'linear-gradient(135deg, rgba(0,245,212,0.03), rgba(0,163,255,0.03))', shadow: 'rgba(0,163,255,0.08)' },
  };
  const c = glowColors[glow] || glowColors.cyan;
  return (
    <div style={{
      borderRadius: 20,
      border: `1px solid ${c.border}`,
      background: typeof c.bg === 'string' && c.bg.includes('gradient') ? c.bg : c.bg,
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      boxShadow: `0 8px 32px ${c.shadow}, inset 0 1px 0 rgba(255,255,255,0.06)`,
      padding: '20px 18px',
      ...style,
    }}>
      {children}
    </div>
  );
}

// ========== MAIN COMPONENT ==========
function BruttoNettoRechner() {
  // Calculator state
  const [inputValue, setInputValue] = useState('');
  const [inputMode, setInputMode] = useState('brutto'); // 'brutto' or 'netto'
  const [mwstRate, setMwstRate] = useState(19);
  const [beschriftung, setBeschriftung] = useState('');
  const [beschreibung, setBeschreibung] = useState('');

  // Saved entries
  const [savedEntries, setSavedEntries] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  });

  // Calculations
  const parsed = parseFloat(inputValue) || 0;
  const mwstFactor = 1 + (mwstRate / 100);
  
  const brutto = inputMode === 'brutto' ? parsed : parsed * mwstFactor;
  const netto = inputMode === 'netto' ? parsed : parsed / mwstFactor;
  const mwstBetrag = brutto - netto;
  const hasInput = parsed > 0;

  // Save
  const handleSave = useCallback(() => {
    if (!hasInput) return;
    const entry = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      beschriftung: beschriftung || '',
      beschreibung: beschreibung || '',
      inputValue: parsed,
      inputMode,
      mwstRate,
      brutto,
      netto,
      mwstBetrag,
    };
    const next = [entry, ...savedEntries].slice(0, 100);
    setSavedEntries(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  }, [hasInput, parsed, inputMode, mwstRate, brutto, netto, mwstBetrag, beschriftung, beschreibung, savedEntries]);

  const handleDelete = useCallback((id) => {
    const next = savedEntries.filter(e => e.id !== id);
    setSavedEntries(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  }, [savedEntries]);

  const handleDeleteAll = useCallback(() => {
    if (confirm('Alle gespeicherten Berechnungen löschen?')) {
      setSavedEntries([]);
      try { localStorage.setItem(STORAGE_KEY, '[]'); } catch {}
    }
  }, []);

  const loadEntry = useCallback((entry) => {
    setInputValue(String(entry.inputValue || ''));
    setInputMode(entry.inputMode || 'brutto');
    setMwstRate(entry.mwstRate || 19);
    setBeschriftung(entry.beschriftung || '');
    setBeschreibung(entry.beschreibung || '');
  }, []);

  const handleReset = useCallback(() => {
    setInputValue('');
    setInputMode('brutto');
    setMwstRate(19);
    setBeschriftung('');
    setBeschreibung('');
  }, []);

  return (
    <section style={{
      maxWidth: 600,
      margin: '0 auto',
      padding: '20px 16px 40px',
      fontFamily: "'SF Pro Display', 'Inter', system-ui, -apple-system, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: 24,
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 56, height: 56,
          borderRadius: 16,
          background: 'linear-gradient(135deg, rgba(0,245,212,0.15), rgba(0,163,255,0.15))',
          border: '1px solid rgba(0,245,212,0.2)',
          marginBottom: 12,
          boxShadow: '0 4px 24px rgba(0,245,212,0.12)',
        }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#00f5d4" strokeWidth="2" style={{ width: 28, height: 28 }}>
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6v12"/>
            <path d="M15 9.5c0-1.38-1.34-2.5-3-2.5S9 8.12 9 9.5c0 2.5 6 1.25 6 3.75 0 1.38-1.34 2.5-3 2.5s-3-1.12-3-2.5"/>
          </svg>
        </div>
        <h2 style={{
          fontSize: '1.4rem',
          fontWeight: 900,
          color: 'var(--sg-text, #e8f0fe)',
          margin: 0,
          letterSpacing: '-0.02em',
        }}>Brutto / Netto Rechner</h2>
        <p style={{
          fontSize: '0.78rem',
          color: 'var(--sg-text-dim, rgba(200,220,255,0.5))',
          margin: '4px 0 0',
        }}>Schnelle Umrechnung mit MwSt · Liquid Glass</p>
      </div>

      {/* Main Calculator */}
      <LiquidGlassCard glow="cyan" style={{ marginBottom: 16 }}>
        {/* Beschriftung & Beschreibung */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--sg-text-dim, rgba(200,220,255,0.6))', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Beschriftung
          </label>
          <input
            type="text"
            value={beschriftung}
            onChange={e => setBeschriftung(e.target.value)}
            placeholder="z.B. Rechnung #4711, Angebot..."
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid rgba(0,245,212,0.12)',
              background: 'rgba(255,255,255,0.04)',
              color: 'var(--sg-text, #e8f0fe)',
              fontSize: '0.85rem',
              fontWeight: 600,
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
            }}
            onFocus={e => e.target.style.borderColor = 'rgba(0,245,212,0.3)'}
            onBlur={e => e.target.style.borderColor = 'rgba(0,245,212,0.12)'}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--sg-text-dim, rgba(200,220,255,0.6))', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Beschreibung <small style={{ opacity: 0.5 }}>(optional)</small>
          </label>
          <textarea
            value={beschreibung}
            onChange={e => setBeschreibung(e.target.value)}
            placeholder="Notizen, Details..."
            rows={2}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid rgba(0,245,212,0.08)',
              background: 'rgba(255,255,255,0.03)',
              color: 'var(--sg-text, #e8f0fe)',
              fontSize: '0.78rem',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Mode Toggle */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,245,212,0.15)' }}>
          {['brutto', 'netto'].map(m => (
            <button key={m} type="button" onClick={() => setInputMode(m)}
              style={{
                flex: 1,
                padding: '10px 0',
                border: 'none',
                background: inputMode === m
                  ? 'linear-gradient(135deg, rgba(0,245,212,0.2), rgba(0,163,255,0.15))'
                  : 'rgba(255,255,255,0.03)',
                color: inputMode === m ? '#00f5d4' : 'var(--sg-text-dim, rgba(200,220,255,0.5))',
                fontWeight: inputMode === m ? 800 : 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textTransform: 'capitalize',
                letterSpacing: '0.02em',
              }}
            >{m === 'brutto' ? 'Brutto → Netto' : 'Netto → Brutto'}</button>
          ))}
        </div>

        {/* Input */}
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--sg-text-dim, rgba(200,220,255,0.6))', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Betrag ({inputMode})
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="number"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder="0,00"
              min="0"
              step="0.01"
              style={{
                width: '100%',
                padding: '14px 50px 14px 18px',
                borderRadius: 14,
                border: '1.5px solid rgba(0,245,212,0.2)',
                background: 'rgba(255,255,255,0.04)',
                color: 'var(--sg-text, #e8f0fe)',
                fontSize: '1.3rem',
                fontWeight: 800,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
              onFocus={e => { e.target.style.borderColor = 'rgba(0,245,212,0.5)'; e.target.style.boxShadow = '0 0 20px rgba(0,245,212,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(0,245,212,0.2)'; e.target.style.boxShadow = 'none'; }}
            />
            <span style={{
              position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)',
              color: 'rgba(0,245,212,0.5)', fontWeight: 800, fontSize: '1.1rem',
            }}>€</span>
          </div>
        </div>

        {/* MwSt Selection */}
        <div style={{ marginBottom: 6 }}>
          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--sg-text-dim, rgba(200,220,255,0.6))', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            MwSt-Satz
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {MWST_OPTIONS.map(opt => (
              <button key={opt.value} type="button" onClick={() => setMwstRate(opt.value)}
                style={{
                  flex: 1,
                  padding: '8px 6px',
                  borderRadius: 10,
                  border: mwstRate === opt.value ? '1.5px solid rgba(0,245,212,0.4)' : '1px solid rgba(255,255,255,0.08)',
                  background: mwstRate === opt.value
                    ? 'linear-gradient(135deg, rgba(0,245,212,0.12), rgba(0,163,255,0.08))'
                    : 'rgba(255,255,255,0.03)',
                  color: mwstRate === opt.value ? '#00f5d4' : 'var(--sg-text-dim, rgba(200,220,255,0.5))',
                  fontWeight: mwstRate === opt.value ? 800 : 600,
                  fontSize: '0.72rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >{opt.label}</button>
            ))}
          </div>
        </div>
      </LiquidGlassCard>

      {/* Results */}
      {hasInput && (
        <LiquidGlassCard glow="dual" style={{ marginBottom: 16 }}>
          {/* Hero Result */}
          <div style={{
            textAlign: 'center',
            padding: '16px 0 12px',
            borderBottom: '1px solid rgba(0,245,212,0.08)',
            marginBottom: 14,
          }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--sg-text-dim, rgba(200,220,255,0.5))', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
              {inputMode === 'brutto' ? 'Netto-Betrag' : 'Brutto-Betrag'}
            </div>
            <div style={{
              fontSize: '2.2rem',
              fontWeight: 900,
              background: 'linear-gradient(135deg, #00f5d4, #00a3ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 1.1,
            }}>
              {inputMode === 'brutto' ? fmtEuro(netto) : fmtEuro(brutto)}
            </div>
          </div>

          {/* Detail Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 12, background: 'rgba(0,245,212,0.04)', border: '1px solid rgba(0,245,212,0.08)' }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--sg-text-dim)', textTransform: 'uppercase', marginBottom: 4 }}>Netto</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--sg-text, #e8f0fe)' }}>{fmtEuro(netto)}</div>
            </div>
            <div style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 12, background: 'rgba(255,0,110,0.04)', border: '1px solid rgba(255,0,110,0.08)' }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--sg-text-dim)', textTransform: 'uppercase', marginBottom: 4 }}>MwSt ({mwstRate}%)</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 800, color: 'rgba(255,0,110,0.8)' }}>{fmtEuro(mwstBetrag)}</div>
            </div>
            <div style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 12, background: 'rgba(0,163,255,0.04)', border: '1px solid rgba(0,163,255,0.08)' }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--sg-text-dim)', textTransform: 'uppercase', marginBottom: 4 }}>Brutto</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--sg-text, #e8f0fe)' }}>{fmtEuro(brutto)}</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" onClick={handleSave} style={{
              flex: 1, padding: '10px 0', borderRadius: 12,
              border: '1px solid rgba(0,245,212,0.3)',
              background: 'linear-gradient(135deg, rgba(0,245,212,0.15), rgba(0,163,255,0.1))',
              color: '#00f5d4', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              Speichern
            </button>
            <button type="button" onClick={handleReset} style={{
              padding: '10px 16px', borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.04)',
              color: 'var(--sg-text-dim)', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer',
            }}>
              ↺ Reset
            </button>
          </div>
        </LiquidGlassCard>
      )}

      {/* Saved Entries */}
      {savedEntries.length > 0 && (
        <LiquidGlassCard glow="purple" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 800, color: 'var(--sg-text, #e8f0fe)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/></svg>
              Gespeicherte ({savedEntries.length})
            </h3>
            <button type="button" onClick={handleDeleteAll} style={{
              padding: '4px 10px', borderRadius: 8,
              border: '1px solid rgba(255,0,110,0.2)',
              background: 'rgba(255,0,110,0.06)',
              color: 'rgba(255,0,110,0.7)', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
            }}>Alle löschen</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {savedEntries.map(entry => (
              <div key={entry.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 12,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                transition: 'border-color 0.2s',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--sg-text, #e8f0fe)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.beschriftung || `${fmtEuro(entry.brutto)} (${entry.mwstRate}%)`}
                  </div>
                  {entry.beschreibung && (
                    <div style={{ fontSize: '0.65rem', color: 'var(--sg-text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                      {entry.beschreibung}
                    </div>
                  )}
                  <div style={{ fontSize: '0.65rem', color: 'var(--sg-text-dim)', marginTop: 2 }}>
                    {fmtEuro(entry.netto)} netto · {fmtEuro(entry.brutto)} brutto · MwSt {fmtEuro(entry.mwstBetrag)}
                  </div>
                  <div style={{ fontSize: '0.58rem', color: 'rgba(200,220,255,0.3)', marginTop: 2 }}>
                    {new Date(entry.timestamp).toLocaleDateString('de-DE')} {new Date(entry.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {/* Edit button */}
                  <button type="button" onClick={() => loadEntry(entry)} title="Bearbeiten" style={{
                    width: 28, height: 28, borderRadius: 8,
                    border: '1px solid rgba(0,245,212,0.2)',
                    background: 'rgba(0,245,212,0.06)',
                    color: '#00f5d4', fontSize: '0.65rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                  {/* Delete button */}
                  <button type="button" onClick={() => handleDelete(entry.id)} title="Löschen" style={{
                    width: 28, height: 28, borderRadius: 8,
                    border: '1px solid rgba(255,0,110,0.2)',
                    background: 'rgba(255,0,110,0.06)',
                    color: 'rgba(255,0,110,0.7)', fontSize: '0.65rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14H7L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </LiquidGlassCard>
      )}

      {/* Quick Reference Table */}
      <LiquidGlassCard glow="cyan" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 10px', fontSize: '0.82rem', fontWeight: 800, color: 'var(--sg-text, #e8f0fe)' }}>
          Schnellreferenz
        </h3>
        <div style={{ fontSize: '0.72rem', color: 'var(--sg-text-dim)', lineHeight: 1.8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
            <span>Brutto → Netto:</span><span style={{ color: 'var(--sg-text)' }}>Brutto ÷ 1,19</span>
            <span>Netto → Brutto:</span><span style={{ color: 'var(--sg-text)' }}>Netto × 1,19</span>
            <span>MwSt aus Brutto:</span><span style={{ color: 'var(--sg-text)' }}>Brutto - (Brutto ÷ 1,19)</span>
            <span>MwSt aus Netto:</span><span style={{ color: 'var(--sg-text)' }}>Netto × 0,19</span>
          </div>
        </div>
      </LiquidGlassCard>
    </section>
  );
}

export default BruttoNettoRechner;
